// Billing-cycle grouping for the in-app plan picker.
//
// A plan sold on more than one cycle arrives as several rows sharing a name
// (they are distinct Plan rows keyed `(name, billingCycle)` server-side), so the
// picker would otherwise show "Power" three times in one flat grid. The cycle
// pill picks which rows are on screen.
//
// Mirrors the landing pricing page (landing/lib/pricing.ts) so the two surfaces
// segment plans the same way.

import type { Plan } from './types';

export type BillingCycle = 'WEEKLY' | 'MONTHLY' | 'YEARLY';
/** Every cycle the system can REPRESENT. WEEKLY stays here on purpose: plans
 *  billed weekly may still exist in the database and a subscriber may still be
 *  on one, so the type must keep parsing and labelling it correctly. */
export const BILLING_CYCLES: BillingCycle[] = ['WEEKLY', 'MONTHLY', 'YEARLY'];

/** Cycles actually OFFERED in the picker. Deliberately narrower than the list
 *  above — this is a commercial decision, not a data one.
 *
 *  Weekly is withdrawn from sale. Doing it here rather than by deleting the
 *  plan rows means an existing weekly subscriber is untouched: their plan still
 *  parses, still renders with the right "Weekly" label, and still shows as their
 *  Current plan (plansForCycle keeps it via keepPlanId). They simply cannot be
 *  sold a new one, and nobody else is shown the option. */
export const OFFERED_CYCLES: BillingCycle[] = ['MONTHLY', 'YEARLY'];
export const CYCLE_LABELS: Record<BillingCycle, string> = {
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  YEARLY: 'Yearly',
};
export const DEFAULT_CYCLE: BillingCycle = 'MONTHLY';

/** Is this cycle still on sale? For surfaces that list the purchase catalog
 *  directly (the /plans page) and have no cycle picker to filter through. */
export function isOfferedCycle(v: unknown): boolean {
  return (OFFERED_CYCLES as string[]).includes(String(v ?? '').toUpperCase());
}

export function asCycle(v: unknown): BillingCycle {
  const s = String(v ?? '').toUpperCase();
  return (BILLING_CYCLES as string[]).includes(s) ? (s as BillingCycle) : DEFAULT_CYCLE;
}

/** Zero-priced plans are cycle-agnostic: Free shows under every option rather
 *  than being duplicated per cycle. */
function isFree(plan: Plan): boolean {
  const n = Number(plan.price);
  return !Number.isFinite(n) || n === 0;
}

/**
 * Cycles worth offering: those with at least one paid plan, in BILLING_CYCLES
 * order. Free rows don't count — they appear under every option, so counting
 * them would open a tab holding nothing but Free.
 *
 * Fewer than two ⇒ callers hide the pill entirely rather than render a single
 * dead option.
 */
export function availableCycles(plans: Plan[]): BillingCycle[] {
  const paid = plans.filter((p) => !isFree(p));
  // OFFERED_CYCLES, not BILLING_CYCLES: a withdrawn cycle never becomes a tab
  // even while paid plans for it still sit in the database.
  return OFFERED_CYCLES.filter((c) => paid.some((p) => asCycle(p.billingCycle) === c));
}

/**
 * The plans to show for `cycle`: that cycle's rows, plus every free row, plus
 * `keepPlanId` whatever its cycle.
 *
 * `keepPlanId` is the caller's CURRENT plan — it must never vanish just because
 * it's billed on a cycle other than the one being browsed, or the picker would
 * show no "Current" card and the user would lose their bearings. Input order is
 * preserved, so the server's ordering carries through.
 */
export function plansForCycle(plans: Plan[], cycle: BillingCycle, keepPlanId?: string | null): Plan[] {
  return plans.filter(
    (p) => isFree(p) || asCycle(p.billingCycle) === cycle || (keepPlanId != null && p.id === keepPlanId),
  );
}

/**
 * Which cycle to open on: the one the caller's current plan is billed on, so
 * their plan is on screen from the first paint. Falls back to Monthly, then to
 * whatever is offered.
 */
export function initialCycle(plans: Plan[], currentPlan?: Plan | null): BillingCycle {
  const cycles = availableCycles(plans);
  if (currentPlan && !isFree(currentPlan)) {
    const c = asCycle(currentPlan.billingCycle);
    if (cycles.includes(c)) return c;
  }
  if (cycles.includes(DEFAULT_CYCLE)) return DEFAULT_CYCLE;
  return cycles[0] ?? DEFAULT_CYCLE;
}
